import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDashboardMt5Overview } from './api/dashboardMt5';
import { useDateFilter } from './context/DateFilterContext';
import { useCellBlink } from './hooks/useCellBlink';
import { useDashboardSocket } from './hooks/useDashboardSocket';
import { useMenuFilterRegistration } from './hooks/useMenuFilterRegistration';
import { useDisabledAccounts } from './hooks/useDisabledAccounts';
import {
  DisabledAccountsBanner,
  DisabledAccountsToggle,
} from './components/DisabledAccountsControls';
import {
  buildLoginNameMap,
  filterLoginRows,
  filterSymbolRows,
} from './utils/accountVisibility';
import {
  ACCOUNT_ACTION_COL,
  accountDisableCell,
  accountEnableCell,
} from './utils/accountTableActions';
import { formatCount, formatKpiValue, formatMoney, isNegativeNumber, mirrorSign } from './utils/format';
import SortableCardGrid from './components/SortableCardGrid/SortableCardGrid';
import {
  DataTable,
  KpiCard,
  DashboardLoadingBanner,
  PeriodRangeBanner,
  closedPeriodDateLabel,
} from './DashboardShared';
import './Dashboard.css';
import './DashboardMt5.css';
export default function DashboardMt5({ active = true }) {
  const [segment, setSegment] = useState('wh');
  const [showDisabledOnly, setShowDisabledOnly] = useState(false);
  const { period, queryFrom, queryTo, historicalMode } = useDateFilter();
  const { disabledSet, disabledLogins, disableLogin, enableLogin, disabledCount } =
    useDisabledAccounts(segment);

  useEffect(() => {
    setShowDisabledOnly(false);
  }, [segment]);

  const fetchOverview = useCallback(
    ({ signal } = {}) =>
      fetchDashboardMt5Overview({
        segment,
        period: historicalMode ? 'today' : period,
        startDate: queryFrom,
        endDate: queryTo,
        signal,
      }),
    [segment, historicalMode, period, queryFrom, queryTo]
  );

  const { data, loading, wsStatus, lastWsLabel, error, reconnect } =
    useDashboardSocket({
      segment,
      period: historicalMode ? 'today' : period,
      startDate: queryFrom,
      endDate: queryTo,
      historicalMode,
      wsPath: '/ws/dashboard/mt5',
      wsIntervalMs: Number(process.env.REACT_APP_DASHBOARD_MT5_WS_INTERVAL_MS) || 100,
      reconnectOnFilterChange: false,
      skipRestWhenWsConnected: true,
      restPollMs: Number(process.env.REACT_APP_DASHBOARD_MT5_REST_POLL_MS) || 90_000,
      enabled: active,
      onFetch: fetchOverview,
    });

  const floating = data?.floating;
  const closed = data?.closed;
  const orders = data?.orders;
  const segmentTabTotals = data?.segment_tab_totals;
  const debugInfo = data?.debug_info;
  const dataSource = data?.data_source || 'mt5_webapi_loading';
  const apiError = data?.error;
  const cashRows = closed?.per_login_cash ?? [];
  const periodDateLabel = closedPeriodDateLabel(closed);
  const floatingRowsRaw = floating?.per_login ?? [];
  const closedRowsRaw = closed?.per_login ?? [];
  const symbolSourceRaw = useMemo(
    () =>
      (floating?.per_symbol ?? []).map((r) => ({
        ...r,
        id: `${r.login}-${r.symbol}`,
      })),
    [floating?.per_symbol]
  );

  const visibilityOpts = useMemo(
    () => ({ showDisabledOnly }),
    [showDisabledOnly]
  );

  const floatingRows = useMemo(
    () => filterLoginRows(floatingRowsRaw, disabledSet, visibilityOpts),
    [floatingRowsRaw, disabledSet, visibilityOpts]
  );
  const closedRows = useMemo(
    () => filterLoginRows(closedRowsRaw, disabledSet, visibilityOpts),
    [closedRowsRaw, disabledSet, visibilityOpts]
  );
  const cashSource = useMemo(
    () =>
      filterLoginRows(
        cashRows.map((r) => ({
          ...r,
          deposit_withdraw: (r.deposit ?? 0) - (r.withdrawal ?? 0),
        })),
        disabledSet,
        visibilityOpts
      ),
    [cashRows, disabledSet, visibilityOpts]
  );
  const symbolSource = useMemo(
    () => filterSymbolRows(symbolSourceRaw, disabledSet, visibilityOpts),
    [symbolSourceRaw, disabledSet, visibilityOpts]
  );

  const cashTotals = useMemo(() => {
    let deposit = 0;
    let withdrawal = 0;
    for (const r of cashSource) {
      const v = Number(r.deposit_withdraw ?? 0);
      if (v > 0) deposit += v;
      else if (v < 0) withdrawal += Math.abs(v);
    }
    return { deposit, withdrawal };
  }, [cashSource]);
  const cashNetTotal = cashTotals.deposit - cashTotals.withdrawal;
  const closedTotal = useMemo(
    () => closedRows.reduce((s, r) => s + Number(r.profit || 0), 0),
    [closedRows]
  );

  const loginNameById = useMemo(
    () => buildLoginNameMap(floatingRowsRaw, closedRowsRaw, cashRows),
    [floatingRowsRaw, closedRowsRaw, cashRows]
  );

  const disabledTableRows = useMemo(() => {
    if (!showDisabledOnly) return [];
    const floatingByLogin = new Map(floatingRowsRaw.map((r) => [Number(r.login), r]));
    const closedByLogin = new Map(closedRowsRaw.map((r) => [Number(r.login), r]));
    return disabledLogins.map((login) => ({
      id: login,
      login,
      name: loginNameById.get(login) || '—',
      floating_pl: floatingByLogin.get(login)?.profit,
      closed_pl: closedByLogin.get(login)?.profit,
    }));
  }, [showDisabledOnly, disabledLogins, floatingRowsRaw, closedRowsRaw, loginNameById]);

  const openPositionCount = floatingRows.length;
  const openPositionLegs =
    orders?.live_position_legs ?? orders?.live_open_positions;
  const liveOpenOrders = orders?.open_orders_count;

  const handleEnableLogin = useCallback(
    (login) => {
      enableLogin(login);
      if (disabledCount <= 1) setShowDisabledOnly(false);
    },
    [enableLogin, disabledCount]
  );

  const mapLoginAction = showDisabledOnly
    ? (login) => accountEnableCell(login, handleEnableLogin)
    : (login) => accountDisableCell(login, disableLogin);

  const floatingBlink = useCellBlink(floatingRows, {
    rowKey: 'login',
    valueKeys: ['profit'],
  });
  const closedBlink = useCellBlink(closedRows, {
    rowKey: 'login',
    valueKeys: ['profit'],
  });
  const cashBlink = useCellBlink(cashSource, {
    rowKey: 'login',
    valueKeys: ['deposit_withdraw'],
  });
  const symbolBlink = useCellBlink(symbolSource, {
    rowKey: 'id',
    valueKeys: ['floating_pl', 'closed_pl'],
  });

  const isMt5Live =
    dataSource === 'mt5_webapi' ||
    dataSource === 'mt5_webapi_ticks' ||
    dataSource === 'mt5_webapi_accounts' ||
    dataSource === 'mt5_webapi_account_profit' ||
    dataSource === 'mt5_webapi_live';

  const isLoadingSource = dataSource === 'mt5_webapi_loading';
  const isLoadingPositions = isLoadingSource && floatingRows.length === 0;

  const closedPlKpi = useMemo(() => {
    if (showDisabledOnly && closedRows.length) {
      return mirrorSign(closedRows.reduce((s, r) => s + Number(r.profit || 0), 0));
    }
    if (!showDisabledOnly && disabledSet.size && closedRowsRaw.length) {
      const visible = filterLoginRows(closedRowsRaw, disabledSet, { showDisabledOnly: false });
      return mirrorSign(visible.reduce((s, r) => s + Number(r.profit || 0), 0));
    }
    return mirrorSign(closed?.total_closed_pl);
  }, [closed?.total_closed_pl, closedRows, closedRowsRaw, disabledSet, showDisabledOnly]);

  const floatingKpiTotal = useMemo(() => {
    if (floatingRows.length) {
      return floatingRows.reduce((s, r) => s + Number(r.profit || 0), 0);
    }
    return floating?.total_floating;
  }, [floating?.total_floating, floatingRows]);

  const liveKpi = wsStatus === 'connected' && !historicalMode;

  const wsLabel = {
    connected:
      dataSource === 'mt5_webapi_accounts' ||
      dataSource === 'mt5_webapi_account_profit' ||
      dataSource === 'mt5_webapi_live'
        ? 'Live MT5 account Profit'
        : isMt5Live
          ? 'Live MT5 ticks'
          : 'Live MT5 WebSocket',
    connecting: 'Connecting MT5 WS…',
    disconnected: 'MT5 REST fallback',
    error: 'MT5 WS error — REST fallback',
    historical: 'MT5 snapshot (past dates)',
    no_token: 'Set REACT_APP_API_TOKEN',
  }[wsStatus] || wsStatus;

  const segmentToolbar = useMemo(
    () => (
      <div className="segment-toggle segment-toggle--inline">
        <button
          type="button"
          className={segment === 'wh' ? 'segment-btn segment-btn--active' : 'segment-btn'}
          onClick={() => setSegment('wh')}
        >
          WH
          {(segmentTabTotals?.wh?.trading_accounts ??
            (segment === 'wh' ? orders?.segment_account_total : null)) != null
            ? ` (${formatKpiValue(
                segmentTabTotals?.wh?.trading_accounts ??
                  (segment === 'wh' ? orders?.segment_account_total : null),
                { integer: true }
              )})`
            : ''}
        </button>
        <button
          type="button"
          className={segment === 'stp' ? 'segment-btn segment-btn--active' : 'segment-btn'}
          onClick={() => setSegment('stp')}
        >
          STP
          {(segmentTabTotals?.stp?.trading_accounts ??
            (segment === 'stp' ? orders?.segment_account_total : null)) != null
            ? ` (${formatKpiValue(
                segmentTabTotals?.stp?.trading_accounts ??
                  (segment === 'stp' ? orders?.segment_account_total : null),
                { integer: true }
              )})`
            : ''}
        </button>
      </div>
    ),
    [segment, segmentTabTotals, orders?.segment_account_total]
  );

  const menuToolbar = useMemo(
    () => (
      <>
        <DisabledAccountsToggle
          showDisabledOnly={showDisabledOnly}
          disabledCount={disabledCount}
          onToggle={() => setShowDisabledOnly((v) => !v)}
        />
        {showDisabledOnly && (
          <DisabledAccountsBanner segment={segment} count={disabledCount} />
        )}
        <span className="status-pill status-pill--source" role="status">
          source: {dataSource}
        </span>
      </>
    ),
    [showDisabledOnly, disabledCount, segment, dataSource]
  );

  useMenuFilterRegistration(active, {
    wsStatus,
    wsLabel,
    lastWsLabel,
    onReconnect: reconnect,
    showReconnect: !historicalMode,
    reconnectLabel: 'Reconnect',
    closed,
    error: error || (!isLoadingPositions ? apiError : null),
    loading: loading && !data,
    extras: segmentToolbar,
    menuToolbar,
  });

  const kpiItems = useMemo(
    () => [
      {
        id: 'floating',
        node: (
          <KpiCard
            label="Floating"
            value={formatKpiValue(floatingKpiTotal)}
            numericValue={floatingKpiTotal}
            sub={liveKpi ? 'live' : undefined}
            large
          />
        ),
      },
      {
        id: 'closed-pl',
        node: (
          <KpiCard
            label="Closed PL"
            value={formatKpiValue(closedPlKpi)}
            numericValue={closedPlKpi}
            sub={periodDateLabel || undefined}
            icon="dollar"
          />
        ),
      },
      {
        id: 'commission',
        node: (
          <KpiCard
            label="Commission"
            value={formatKpiValue(closed?.total_commission)}
            numericValue={closed?.total_commission}
          />
        ),
      },
      {
        id: 'swap',
        node: (
          <KpiCard
            label="Swap"
            value={formatKpiValue(closed?.total_swap)}
            numericValue={closed?.total_swap}
          />
        ),
      },
      {
        id: 'deposit',
        node: (
          <KpiCard
            label="Deposit"
            value={formatKpiValue(cashTotals.deposit)}
            numericValue={cashTotals.deposit}
            sub={periodDateLabel || undefined}
          />
        ),
      },
      {
        id: 'withdrawal',
        node: (
          <KpiCard
            label="Withdrawal"
            value={formatKpiValue(cashTotals.withdrawal)}
            numericValue={cashTotals.withdrawal}
            sub={periodDateLabel || undefined}
          />
        ),
      },
      {
        id: 'open-positions',
        node: (
          <KpiCard
            label="Open Positions"
            value={formatKpiValue(openPositionLegs, { integer: true })}
            numericValue={openPositionLegs}
            sub={liveKpi ? 'live' : undefined}
          />
        ),
      },
      {
        id: 'open-orders',
        node: (
          <KpiCard
            label="Open Orders"
            value={formatKpiValue(liveOpenOrders, { integer: true })}
            numericValue={liveOpenOrders}
            sub={liveKpi ? 'live' : undefined}
          />
        ),
      },
    ],
    [floatingKpiTotal, closed, closedPlKpi, orders, openPositionLegs, liveOpenOrders, cashTotals, liveKpi, periodDateLabel]
  );

  const tableItems = useMemo(() => {
    if (showDisabledOnly) {
      return [
        {
          id: 'disabled-accounts-table',
          node: (
            <DataTable
              title={`Disabled accounts (${disabledCount})`}
              sortKey="mt5-disabled"
              enableColumnFilters
              columns={[
                { key: 'login', label: 'Login' },
                { key: 'name', label: 'Name' },
                { key: 'floating_pl', label: 'Floating P/L', className: 'num' },
                { key: 'closed_pl', label: 'Closed P/L', className: 'num' },
                ACCOUNT_ACTION_COL,
              ]}
              rows={disabledTableRows.map((r) => ({
                id: r.login,
                login: r.login,
                name: r.name,
                floating_pl: r.floating_pl != null ? formatMoney(r.floating_pl) : '—',
                closed_pl: r.closed_pl != null ? formatMoney(r.closed_pl) : '—',
                _action: mapLoginAction(r.login),
                __numeric: {
                  login: Number(r.login),
                  floating_pl: Number(r.floating_pl ?? 0),
                  closed_pl: Number(r.closed_pl ?? 0),
                },
              }))}
            />
          ),
        },
      ];
    }

    const loginCols = (valueKey, valueLabel) => [
      { key: 'login', label: 'Login' },
      { key: 'name', label: 'Name' },
      { key: valueKey, label: valueLabel, className: 'num' },
      ACCOUNT_ACTION_COL,
    ];

    return [
      {
        id: 'floating-table',
        node: (
          <DataTable
            title={`Floating open (${openPositionCount})`}
            sortKey="mt5-floating"
            blinkCells={floatingBlink}
            enableColumnFilters
            columns={loginCols('profit', 'Floating P/L')}
            rows={floatingRows.map((r) => ({
              id: r.login,
              login: r.login,
              name: r.name || '—',
              profit: formatMoney(r.profit),
              _action: mapLoginAction(r.login),
              __numeric: { profit: Number(r.profit), login: Number(r.login) },
            }))}
          />
        ),
      },
      {
        id: 'closed-table',
        node: (
          <DataTable
            title={
              periodDateLabel
                ? `Closed PL Login wise · ${periodDateLabel}`
                : 'Closed PL Login wise'
            }
            sortKey="mt5-closed"
            blinkCells={closedBlink}
            enableColumnFilters
            columns={loginCols('profit', 'Closed PL')}
            rows={closedRows.map((r) => ({
              id: r.login,
              login: r.login,
              name: r.name || '—',
              profit: formatMoney(r.profit),
              _action: mapLoginAction(r.login),
              __numeric: { profit: Number(r.profit), login: Number(r.login) },
            }))}
            footer={
              <tr className="total-row">
                <td colSpan={3}>Total</td>
                <td className="num">{formatMoney(closedTotal)}</td>
              </tr>
            }
          />
        ),
      },
      {
        id: 'symbol-table',
        node: (
          <DataTable
            title="Symbol"
            sortKey="mt5-symbol"
            blinkCells={symbolBlink}
            enableColumnFilters
            columns={[
              { key: 'symbol', label: 'Symbol' },
              { key: 'login', label: 'Login' },
              { key: 'floating_pl', label: 'Floating PL', className: 'num' },
              { key: 'closed_pl', label: 'Closed PL', className: 'num' },
            ]}
            rows={symbolSource.map((r) => ({
              id: r.id,
              symbol: r.symbol,
              login: r.login,
              floating_pl: formatMoney(r.floating_pl),
              closed_pl: formatMoney(r.closed_pl),
              __numeric: {
                floating_pl: Number(r.floating_pl),
                closed_pl: Number(r.closed_pl),
                login: Number(r.login),
              },
            }))}
          />
        ),
      },
      {
        id: 'cash-table',
        node: (
          <DataTable
            title={
              periodDateLabel
                ? `Deposit & Withdrawal (${cashSource.length}) · ${periodDateLabel}`
                : `Deposit & Withdrawal (${cashSource.length})`
            }
            sortKey="mt5-cash"
            blinkCells={cashBlink}
            enableColumnFilters
            columns={loginCols('deposit_withdraw', 'Deposit/Withdraw')}
            rows={cashSource.map((r) => ({
              id: r.login,
              login: r.login,
              name: r.name || '—',
              deposit_withdraw: formatMoney(r.deposit_withdraw),
              _action: mapLoginAction(r.login),
              __numeric: {
                deposit_withdraw: Number(r.deposit_withdraw),
                login: Number(r.login),
              },
            }))}
            footer={
              <tr className="total-row">
                <td colSpan={3}>Total</td>
                <td className={`num${isNegativeNumber(cashNetTotal) ? ' num--neg' : ''}`}>
                  {formatMoney(cashNetTotal)}
                </td>
              </tr>
            }
          />
        ),
      },
    ];
  }, [
    showDisabledOnly,
    disabledCount,
    disabledTableRows,
    ACCOUNT_ACTION_COL,
    mapLoginAction,
    floatingRows,
    openPositionCount,
    closedRows,
    symbolSource,
    cashSource,
    floatingBlink,
    closedBlink,
    symbolBlink,
    cashBlink,
    closedTotal,
    cashNetTotal,
    periodDateLabel,
  ]);

  return (
    <div
      className={`godo-dashboard godo-dashboard--mt5${
        historicalMode ? ' godo-dashboard--historical' : ''
      }`}
    >
      <DashboardLoadingBanner
        fullPage
        loading={loading && !data}
        label={historicalMode ? 'Loading historical data…' : 'Loading period data…'}
      />
      {debugInfo && (
        <details className="mt5-debug-panel">
          <summary>MT5 debug (F12 console for full log)</summary>
          <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
        </details>
      )}

      <PeriodRangeBanner closed={closed} historical={historicalMode} />

      <div className={`dashboard-content${loading && floatingRows.length === 0 ? ' dashboard-content--loading' : ''}`}>
      <SortableCardGrid storageKey="mt5-kpi-order" className="kpi-grid" section="kpi" items={kpiItems} />
      <SortableCardGrid storageKey="mt5-tables-order" className="tables-row" section="tables" items={tableItems} />
      </div>
    </div>
  );
}
